import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import { Grid, makeStyles, TextField } from '@material-ui/core';

import { useCommonBenchmarks } from 'hooks/useCommonBenchmarks';

import TabPanel from 'components/TabPanel';
import BenchmarkSelector from './BenchmarkSelector';
import MenuControls from './MenuControls';
import AdvancedBenchmarkSelector from './AdvancedBenchmarkSelector';
import BenchmarkIndexList from './BenchmarkIndexList';

const MAX_BENCHMARK_COUNT = 6;

const generateEmptyRecords = arr => {
  const validRecords = arr || [];
  const emptyRecords = [
    ...Array(MAX_BENCHMARK_COUNT - arr.length).fill({
      benchmarkId: null,
      name: ''
    })
  ];

  return [...validRecords, ...emptyRecords];
};

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '700px',
    height: '400px'
  },
  section: {
    height: '100%'
  },
  subSection: {
    flexGrow: '1'
  },
  defaultFieldInput: {
    padding: '0 4px',
    height: '40px',
    color: theme.palette.grey.dark,
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    }
  }
}));

const BenchmarksTab = ({
  value,
  benchmarks,
  handleClose,
  onSubmit,
  dashboardData,
  handlePropUpdate
}) => {
  const classes = useStyles();
  const [advancedViewIndex, setAdvancedViewIndex] = useState();
  const [selectedBenchmarks, setSelectedBenchmarks] = useState(generateEmptyRecords(benchmarks));
  const [, filteredBenchmarks, addToResults] = useCommonBenchmarks(selectedBenchmarks);

  const handleSubmit = () => {
    const selectedCopy = selectedBenchmarks.filter(b => !!b.benchmarkId);
    const formData = {
      benchmarks: selectedCopy.map(b => b.benchmarkId)
    };

    setSelectedBenchmarks(generateEmptyRecords(selectedCopy));

    onSubmit(formData);
  };

  const updateBenchmarkState = bms => {
    const e = {
      target: {
        value: bms.filter(x => !!x),
        name: 'benchmarks'
      },
      persist: () => {}
    };
    handlePropUpdate(e);
  };
  const onBenchmarkChanged = (updatedBenchmark, index) => {
    if (updatedBenchmark.name) {
      const dropdownOptionExists = filteredBenchmarks.some(
        fb => fb.label === updatedBenchmark.name && fb.value === updatedBenchmark.benchmarkId
      );
      if (!dropdownOptionExists) {
        addToResults(updatedBenchmark);
      }

      const updatedItems = [...selectedBenchmarks];
      updatedItems[index] = updatedBenchmark;
      updateBenchmarkState(updatedItems);
      setSelectedBenchmarks(updatedItems);
    } else {
      const updatedItems = selectedBenchmarks.map(fb => {
        if (fb.benchmarkId === updatedBenchmark.benchmarkId) {
          return { benchmarkId: null, name: '' };
        }
        return fb;
      });
      setSelectedBenchmarks(updatedItems);
    }
  };

  const onAdvancedButtonClick = index => {
    setAdvancedViewIndex(index);
  };

  const clearAdvancedView = () => {
    setAdvancedViewIndex();
  };

  const renderDefaultBenchmarks = () => {
    return generateEmptyRecords(dashboardData.benchmarks).map(benchmark => (
      <Grid item key={`default-${benchmark.benchmarkId || nanoid(6)}`}>
        <TextField
          value={benchmark.name}
          InputProps={{ className: classes.defaultFieldInput }}
          disabled
        />
      </Grid>
    ));
  };

  const renderSelectedBenchmarks = options => {
    return selectedBenchmarks.map((benchmark, index) => (
      <Grid
        item
        key={`selected-${benchmark.benchmarkId || nanoid(6)}`}
        container
        alignItems="center"
      >
        <BenchmarkSelector
          index={index}
          selected={benchmark.benchmarkId}
          options={[{ label: benchmark.name, value: benchmark.benchmarkId }, ...options]}
          onSelected={onBenchmarkChanged}
          onAdvancedButtonClick={onAdvancedButtonClick}
        />
      </Grid>
    ));
  };

  const renderRightColumn = () => {
    if (advancedViewIndex !== undefined) {
      return (
        <AdvancedBenchmarkSelector
          index={advancedViewIndex}
          onBenchmarkAccepted={onBenchmarkChanged}
          clearAdvancedView={clearAdvancedView}
        />
      );
    }

    return (
      <Grid item container direction="row" spacing={1} className={classes.subSection}>
        <Grid item container xs={1}>
          <BenchmarkIndexList count={6} advancedView={false} />
        </Grid>
        <Grid item container xs={11}>
          {filteredBenchmarks && renderSelectedBenchmarks(filteredBenchmarks)}
        </Grid>
      </Grid>
    );
  };

  return (
    <TabPanel value={value} index={2} style={{ minWidth: '700px' }}>
      <Grid
        container
        direction="row"
        justify="center"
        alignItems="flex-start"
        spacing={2}
        style={{ paddingLeft: '15px' }}
        className={classes.root}
      >
        <Grid item container direction="column" xs={12} md={4} className={classes.section}>
          <h3>Default benchmarks</h3>
          <Grid item container direction="row" className={classes.subSection}>
            <Grid item container xs={11}>
              {renderDefaultBenchmarks()}
            </Grid>
          </Grid>
        </Grid>

        <Grid item container direction="column" xs={12} md={8} className={classes.section}>
          <h3>Choose benchmarks</h3>
          {renderRightColumn()}
        </Grid>
      </Grid>
      <MenuControls handleClose={handleClose} useFormSubmit={false} onSubmit={handleSubmit} />
    </TabPanel>
  );
};

BenchmarksTab.propTypes = {
  value: PropTypes.number.isRequired,
  benchmarks: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      benchmarkId: PropTypes.number.isRequired
    })
  ).isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dashboardData: PropTypes.shape({
    handlePropUpdate: PropTypes.func,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectTypeId: PropTypes.number.isRequired,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        benchmarkId: PropTypes.number.isRequired
      })
    )
  })
};

BenchmarksTab.defaultProps = {
  dashboardData: {},
  handlePropUpdate: () => {}
};

export default BenchmarksTab;
